import type {
  DynamicFormState,
  FieldConfig,
  FieldType,
  FormConfig,
  GroupFieldConfig,
  SelectFieldConfig,
  SwitchFieldConfig,
  TextFieldConfig
} from './form.types'
import type { GenericObject } from '~/types/common'

export const isTextField = (field: FieldConfig): field is TextFieldConfig => {
  return field.type === 'text'
}

export const isSelectField = (field: FieldConfig): field is SelectFieldConfig => {
  return field.type === 'select'
}

export const isSwitchField = (field: FieldConfig): field is SwitchFieldConfig => {
  return field.type === 'switch'
}

export const isGroupField = (field: FieldConfig): field is GroupFieldConfig => {
  return field.type === 'group'
}

export type LeafFieldConfig = Exclude<FieldConfig, GroupFieldConfig>

export const flattenFields = (fields: FieldConfig[]): LeafFieldConfig[] =>
  fields.flatMap((field) => (field.type === 'group' ? flattenFields(field.children) : [field]))

export const getDefaultValueByFieldType = (type: FieldType): unknown => {
  if (type === 'text' || type === 'select') {
    return ''
  }

  return null
}

export const filterVisibleFields = (fields: FieldConfig[], values: GenericObject): FieldConfig[] => {
  return fields
    .filter((field) => {
      return typeof field.visibleIf !== 'function' || field.visibleIf(values)
    })
    .map((field) => {
      if (field.type === 'group') {
        return {
          ...field,
          children: filterVisibleFields(field.children, values)
        }
      }
      return field
    })
}

export const filterPayloadByVisibleFields = (fields: FieldConfig[], payload: GenericObject): GenericObject => {
  const visibleFields = flattenFields(filterVisibleFields(fields, payload))
  const visibleFieldIds = new Set(visibleFields.map((field) => field.id))
  const filteredPayload: GenericObject = {}

  for (const key in payload) {
    if (visibleFieldIds.has(key)) {
      filteredPayload[key] = payload[key]
    }
  }

  return filteredPayload
}

export const validatePayload = (
  formConfig: FormConfig,
  formState: DynamicFormState,
  payload: GenericObject
): boolean => {
  clearFieldErrors(formConfig.fields, formState)

  let schema = formConfig.schema
  if (typeof schema === 'function') {
    schema = schema(payload)
  }

  if (schema) {
    const result = schema.safeParse(payload)
    if (!result.success) {
      for (const zodError of result.error.issues) {
        const key = zodError.path[0] as string
        if (formState[key]) formState[key].error = zodError.message
      }
      return false
    }
  }

  return true
}

export const prevalidateState = (formConfig: FormConfig, formState: DynamicFormState): boolean => {
  const payload = getPayloadFromState(formState)
  let schema = formConfig.schema
  if (typeof schema === 'function') {
    schema = schema(payload)
  }

  if (schema) {
    const result = schema.safeParse(payload)
    if (!result.success) {
      return false
    }
  }

  return true
}

const getPayloadFromState = (formState: DynamicFormState): GenericObject => {
  const payload: GenericObject = {}

  for (const key in formState) {
    payload[key] = formState[key].value
  }

  return payload
}

const clearFieldErrors = (fields: FieldConfig[], formState: DynamicFormState): void => {
  if (!fields || fields.length === 0) return

  for (const field of fields) {
    if (field.type === 'group') {
      clearFieldErrors(field.children, formState)
    } else if (formState[field.id]) {
      formState[field.id].error = null
    }
  }
}
